<script setup lang="ts">
import type { INewsDataClientDTO } from '~/shared/api/client/news/news.dto';
import type { ISpecialProjectYearItemDataClientDTO } from '~/shared/api/client/special_project_year/special_project_year.dto';
import { getMaterialPublicId } from '~/shared/lib/getMaterialLink';
import { resizeCropSlot } from '~/shared/lib/resizeImage';
import { CROP_RATIO_SPECIAL } from '~/shared/lib/crop-ratio.const';
import { SPECIAL_ITEM_IMAGE_WIDTH } from '../lib/const';

const props = defineProps<
  (INewsDataClientDTO | ISpecialProjectYearItemDataClientDTO) & {
    textColor: string;
    utm?: string;
  }
>();

const isExpert = computed(
  () => props.expert !== null && props.expert !== undefined,
);

const isRedirect = computed(() =>
  Boolean(props.news?.redirect && props.news.redirect.length > 0),
);

const link = computed(() => {
  if (isRedirect.value && props.news?.redirect)
    return props.news.redirect + (props?.utm ?? '');
  if (isExpert.value)
    return `/expert/${getMaterialPublicId(props.expert!)}${props?.utm ?? ''}`;
  return `/${props.news?.type}/${props.news?.id}${props?.utm ?? ''}`;
});

const imageOne = computed(
  () =>
    props.image_one?.file_url ||
    props.news?.image?.photo.file_url ||
    props.expert?.image?.photo.file_url ||
    undefined,
);
const imageAttrs = computed(() =>
  resizeCropSlot(imageOne.value, CROP_RATIO_SPECIAL.wide, 0, {
    width: SPECIAL_ITEM_IMAGE_WIDTH,
  }),
);
</script>

<template>
  <div
    :class="[$style.item]"
    :style="{
      '--text-color': textColor,
    }"
  >
    <div :class="$style.item__top">
      <NuxtLink :class="$style.item__title" :to="link">
        {{ props.title || props.news?.title || props.expert?.title }}
      </NuxtLink>

      <NuxtLink
        v-if="props.image_one"
        :class="$style.item__imageWrapper"
        :to="link"
        :target="isRedirect ? '_blank' : undefined"
      >
        <img
          :src="imageAttrs.src"
          :srcset="imageAttrs.srcset || undefined"
          :alt="title"
          :class="$style.item__imageWrapper"
        />
      </NuxtLink>
    </div>
    <div :class="$style.item__subject">
      {{ subject }}
    </div>
  </div>
</template>

<style lang="scss" module>
.item {
  display: flex;
  flex-direction: column;
  gap: var(--space-1x);

  &__top {
    display: flex;
    flex-flow: row nowrap;
    gap: var(--space-2-5x);
  }

  &__title {
    display: block;
    @include text_h4;
    color: var(--text-color);
    text-decoration: none;
    flex-grow: 1;
    align-self: stretch;
    align-content: center;
  }

  &__subject {
    display: block;
    @include text_body1_large_medium;
    @include line_clamp;
    color: var(--text-color);
    text-decoration: none;
    flex-grow: 1;
    align-self: center;
  }

  &__image {
    width: 320;
    height: auto;

    &Wrapper {
      display: flex;
      position: relative;
      width: 320px;
      height: 180px;
      object-fit: cover;
    }
  }
}

@media (hover: hover) {
  .item {
    transition: all 0.2s;

    &:hover {
      opacity: 0.85;
    }
  }
}

@media (max-width: 600px) {
  .item {
    &__top {
      flex-direction: column;
    }
    &__image {
      &Wrapper {
        width: 100%;
        height: 100%;
      }
    }
  }
}
@media (max-width: 479px) {
}
</style>
